/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */


/**
 * The shared line grammar: "{subject} was|were {verb}[ {suffix}][ by {actor}]{aside}.[ {trailer}]"
 *
 * Descriptions are generated from facts rather than stored, so no per-type sentence template
 * exists anywhere — not in a data file, not in a column. A type's supplier says what changed and
 * this decides the wording.
 *
 * The daily-summary email builds the same shape from the same copy contract
 * (`documentation/Specifications/Activity Description Copy Contract.md`) in its own PHP half,
 * `server/app/lib/App/Utils/Activity/ActivityLine.php`, which is what keeps the two renderers from
 * drifting the way the species copy did. `tests/preflight/check-activity-grammar.js` runs one set of
 * wording assertions through both, and evaluates this file without a build — so keep it
 * dependency-free; the gate asserts that property rather than assuming it.
 *
 * Returns the parts instead of a finished string for two reasons. `subject` is frequently a React
 * node — a link, or the species modal opener — so it cannot be concatenated. And the actor clause
 * has to land inside the first sentence, before the second sentence that some types carry, which a
 * plain append cannot express.
 *
 * Punctuation follows a single rule: whoever completes the sentence owns its terminal period.
 * `copula`/`verb`/`suffix` therefore never carry one, and the renderer adds it after the actor
 * clause — so a line reads "… was added by Dana Whitfield." with an actor and "… was added." without.
 *
 * Two slots exist for lines the plain shape cannot say:
 *
 * `copula` overrides was/were. The permit renewal warning is the only type whose sentence is not
 * about something that happened — "must be renewed by <date>" — and a modal copula is the whole of
 * that difference, so it needs no separate renderer.
 *
 * `aside` lands after the actor clause but inside the sentence, for a parenthetical that qualifies
 * the whole clause rather than the verb — reviewer notes on a survey status change read "was
 * returned with changes requested by Dana Whitfield: "…"." It attaches with no separator, so the
 * supplier owns its leading punctuation; `suffix` is the slot for anything that belongs before the
 * actor.
 */
export const buildActivityLine = (facts) => {
    const {
        subject,
        verb,
        plural = false,
        copula = null,
        suffix = null,
        aside = null,
        trailer = null,
        actor = null,
    } = facts ?? {};

    return {
        subject,
        copula: copula ?? (plural ? 'were' : 'was'),
        verb,
        suffix,
        aside,
        trailer,
        actor,
    };
};

/**
 * Flattens a line to plain text. Only meaningful when `subject` and `trailer` are strings, which
 * makes it the grammar's test seam — assertions can check wording without rendering React — and
 * the shape the email's builder mirrors.
 */
export const activityLineToText = (line) => {
    const {subject, copula, verb, suffix, aside, trailer, actor} = line ?? {};

    const core = [subject, copula, verb, suffix].filter(Boolean).join(' ');
    const attributed = actor ? `${core} by ${actor}` : core;
    const sentence = `${attributed}${aside ?? ''}.`;

    return trailer ? `${sentence} ${trailer}` : sentence;
};
